/**
 * KPI Cards - Key Performance Indicators
 * Shows critical metrics: TTM Funding, TTM M&A, YTD Funding, YTD M&A
 */

import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { formatMonetaryValue } from '../utils/chartUtils';

/**
 * Format currency values for KPI cards with 2 decimal places
 */
const formatKPICurrency = (value: number): string => {
  // Handle zero and invalid values
  if (!value || value === 0) {
    return '$0.00';
  }

  const absValue = Math.abs(value);

  // Values in billions (>= 1B)
  if (absValue >= 1000000000) {
    const billions = value / 1000000000;
    return `$${billions.toFixed(2)}B`;
  }

  // Values in millions (>= 1M)
  if (absValue >= 1000000) {
    const millions = value / 1000000;
    return `$${millions.toFixed(2)}M`;
  }

  // Values in thousands (>= 1K)
  if (absValue >= 1000) {
    const thousands = value / 1000;
    return `$${thousands.toFixed(2)}K`;
  }

  return `$${value.toFixed(2)}`;
};

interface KPIMetric {
  label: string;
  value: number;
  count?: number;
  previousValue?: number;
  previousCount?: number;
  period: string;
  type: 'currency' | 'count';
}

interface KPICategoryMetric {
  label: string;
  value: number;
  name: string; // Category name
  count: number;
  period: string;
  type: 'currency' | 'count';
}

interface KPICardsProps {
  ttmFunding: KPIMetric;
  ttmMA: KPIMetric;
  ytdFunding: KPIMetric;
  ytdMA: KPIMetric;
  topFundedCategory?: KPICategoryMetric;
  topAcquiredCategory?: KPICategoryMetric;
  ttmTopFundedCategory?: KPICategoryMetric;
  ttmTopAcquiredCategory?: KPICategoryMetric;
  ttmAverageDealSize?: KPIMetric;
  ytdAverageDealSize?: KPIMetric;
  ttmMADollarValue?: KPIMetric;
  ytdMADollarValue?: KPIMetric;
}

const KPICategoryCard: React.FC<{ metric: KPICategoryMetric }> = ({ metric }) => {
  const { label, value, name, count, period, type } = metric;
  const formattedValue = type === 'currency' ? formatKPICurrency(value) : value.toLocaleString();

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow">
      {/* Label */}
      <div className="text-xs sm:text-sm font-medium text-gray-600 mb-2">
        {label}
      </div>

      {/* Category Name */}
      <div className="text-lg sm:text-2xl font-bold text-purple-600 mb-2">
        {name}
      </div>

      {/* Main Value */}
      <div className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
        {formattedValue}
      </div>

      {/* Deal Count */}
      <div className="text-sm text-gray-600 mb-1">
        {count.toLocaleString()} {count === 1 ? 'deal' : 'deals'}
      </div>

      {/* Period */}
      <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
        {period}
      </div>
    </div>
  );
};

const KPICard: React.FC<{ metric: KPIMetric }> = ({ metric }) => {
  const { label, value, count, previousValue, previousCount, period, type } = metric;

  // Calculate change percentage
  const hasComparison = previousValue !== undefined && previousValue > 0;
  const changePercent = hasComparison
    ? ((value - previousValue!) / previousValue!) * 100
    : 0;
  const isPositive = changePercent > 0;
  const isNegative = changePercent < 0;
  const isFlat = Math.abs(changePercent) < 1;

  const formattedValue = type === 'currency' ? formatKPICurrency(value) : value.toLocaleString();
  const formattedPrevious = previousValue !== undefined
    ? (type === 'currency' ? formatKPICurrency(previousValue) : previousValue.toLocaleString())
    : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow">
      {/* Label */}
      <div className="text-xs sm:text-sm font-medium text-gray-600 mb-2">
        {label}
      </div>

      {/* Main Value */}
      <div className="flex items-baseline gap-2 mb-2">
        <div className="text-2xl sm:text-3xl font-bold text-gray-900">
          {formattedValue}
        </div>

        {hasComparison && !isFlat && (
          <div className={`flex items-center gap-1 text-sm font-semibold ${
            isPositive ? 'text-green-600' : 'text-red-600'
          }`}>
            {isPositive ? (
              <TrendingUp className="w-4 h-4" />
            ) : (
              <TrendingDown className="w-4 h-4" />
            )}
            {Math.abs(changePercent).toFixed(0)}%
          </div>
        )}

        {isFlat && (
          <div className="flex items-center gap-1 text-sm font-semibold text-gray-500">
            <Minus className="w-4 h-4" />
            Flat
          </div>
        )}
      </div>

      {/* Deal Count (if applicable) */}
      {count !== undefined && (
        <div className="text-sm text-gray-600 mb-1">
          {count.toLocaleString()} {count === 1 ? 'deal' : 'deals'}
        </div>
      )}

      {/* Comparison */}
      {hasComparison && (
        <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
          vs {formattedPrevious} {period}
          {previousCount !== undefined && ` (${previousCount.toLocaleString()} deals)`}
        </div>
      )}

      {/* Period indicator for YTD */}
      {!hasComparison && (
        <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
          {period}
        </div>
      )}
    </div>
  );
};

export const KPICards: React.FC<KPICardsProps> = ({
  ttmFunding,
  ttmMA,
  ytdFunding,
  ytdMA,
  topFundedCategory,
  topAcquiredCategory,
  ttmTopFundedCategory,
  ttmTopAcquiredCategory,
  ttmAverageDealSize,
  ytdAverageDealSize,
  ttmMADollarValue,
  ytdMADollarValue
}) => {
  return (
    <div className="space-y-4 mb-6">
      {/* Primary metrics - TTM and YTD */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard metric={ttmFunding} />
        <KPICard metric={ttmMA} />
        <KPICard metric={ytdFunding} />
        <KPICard metric={ytdMA} />
      </div>

      {/* Category metrics - All 4 in one row */}
      {(ttmTopFundedCategory || ttmTopAcquiredCategory || topFundedCategory || topAcquiredCategory) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {ttmTopFundedCategory && <KPICategoryCard metric={ttmTopFundedCategory} />}
          {ttmTopAcquiredCategory && <KPICategoryCard metric={ttmTopAcquiredCategory} />}
          {topFundedCategory && <KPICategoryCard metric={topFundedCategory} />}
          {topAcquiredCategory && <KPICategoryCard metric={topAcquiredCategory} />}
        </div>
      )}

      {/* Deal Economics metrics - Deal sizes and M&A dollar values */}
      {/* Consistent ordering: TTM Funding → TTM M&A → YTD Funding → YTD M&A */}
      {(ttmAverageDealSize || ytdAverageDealSize || ttmMADollarValue || ytdMADollarValue) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {ttmAverageDealSize && <KPICard metric={ttmAverageDealSize} />}
          {ttmMADollarValue && <KPICard metric={ttmMADollarValue} />}
          {ytdAverageDealSize && <KPICard metric={ytdAverageDealSize} />}
          {ytdMADollarValue && <KPICard metric={ytdMADollarValue} />}
        </div>
      )}
    </div>
  );
};

export default KPICards;
